import { useQuery } from '@tanstack/react-query';
import { api, type UploadedFile, type UserRef } from './api';

/**
 * Viganium Drive (operator, 2026-11-02) — what the API sends about folders and
 * files, and the few things every Drive page shares. Everything is private to
 * its owner until they share it: with employees (any of them, whatever their
 * department), with departments (whoever is in one now), with everyone, by a
 * public link, or by email to a client's contacts. Whoever it is shared with to
 * edit shares it too.
 */

export type DriveKind = 'FOLDER' | 'FILE';
export type DriveRole = 'VIEW' | 'EDIT';
/** How the reader reaches an item: as its owner, or as it was shared with them. */
export type DriveAccess = 'OWNER' | DriveRole;
/**
 * How a page names it: an administrator (`drive.view_all`) reaches every drive —
 * to open and to share — and is shown as such on what is not theirs.
 */
export type DriveShownAccess = DriveAccess | 'ADMIN';

export interface DrivePlace {
  id: string;
  name: string;
}

/** A row of any Drive list. */
export interface DriveItem {
  id: string;
  kind: DriveKind;
  name: string;
  parentId: string | null;
  createdAt: string;
  updatedAt: string;
  trashedAt: string | null;
  owner: UserRef;
  addedBy: UserRef | null;
  binnedBy: UserRef | null;
  client: { id: string; code: string; companyName: string; companyNameAr: string | null } | null;
  project: { id: string; code: string; name: string; nameAr: string | null } | null;
  /** A file's bytes, through signed links. */
  file: UploadedFile | null;
  /** What is in a folder. */
  items: number | null;
  mine: boolean;
  /** Its owner's, and an administrator's: what it is shared with, for the list's marks. */
  shared: { people: number; departments: number; everyone: DriveRole | null; publicLink: boolean; contacts: number } | null;
  access: DriveShownAccess;
  /** What the reader may do to it from a list. */
  can: { edit: boolean; share: boolean };
  /** Where it is — the folders above it the reader may be shown. Empty in a folder's own list. */
  location: DrivePlace[];
}

/** A row of Everyone's drives — an administrator's. */
export interface DriveOwnerSummary {
  owner: UserRef & { isActive: boolean };
  items: number;
  files: number;
  bytes: number;
  updatedAt: string;
}

export interface DriveLink {
  id: string;
  kind: 'PUBLIC' | 'CONTACT';
  url: string;
  expiresAt: string | null;
  /** It opens: not stopped, not expired, its contact not removed. */
  live: boolean;
  expired: boolean;
  contact: {
    id: string;
    name: string;
    jobTitle: string | null;
    removed: boolean;
    client: { id: string; companyName: string; companyNameAr: string | null };
  } | null;
  email: string | null;
  name: string | null;
  message: string | null;
  createdAt: string;
  createdBy: UserRef | null;
  openCount: number;
  lastOpenedAt: string | null;
  downloadCount: number;
  lastDownloadedAt: string | null;
}

/** Everything an item is shared with — shown to whoever shares it: its owner, its editors, an administrator. */
export interface DriveSharing {
  everyoneRole: DriveRole | null;
  people: Array<{ user: UserRef & { isActive: boolean }; role: DriveRole; sharedAt: string }>;
  /** Whoever is in one of these now reaches it (operator, 2026-11-03). */
  departments: Array<{ department: DriveDepartmentRef & { isActive: boolean }; role: DriveRole; sharedAt: string }>;
  publicLink: DriveLink | null;
  contactLinks: DriveLink[];
  /**
   * The folders above it that are shared: it is shared through them too — as far
   * up as the reader is shown them, and whether they may share each.
   */
  through: Array<{
    id: string;
    name: string;
    everyoneRole: DriveRole | null;
    people: number;
    departments: number;
    canShare: boolean;
  }>;
}

export interface DriveDepartmentRef {
  id: string;
  name: string;
  nameAr: string | null;
}

/** `GET /drive/departments`: every active department, how many are in it, and whether the reader is. */
export interface DriveDepartmentOption extends DriveDepartmentRef {
  code: string;
  people: number;
  mine: boolean;
}

/** `GET /drive/items/:id`, and a folder's head. */
export interface DriveDetails extends Omit<DriveItem, 'access' | 'location' | 'can'> {
  access: DriveShownAccess;
  binned: boolean;
  path: DrivePlace[];
  can: {
    add: boolean;
    edit: boolean;
    trash: boolean;
    share: boolean;
    shareOutside: boolean;
    restore: boolean;
    purge: boolean;
  };
  sharing: DriveSharing | null;
}

export interface DriveRelatedOptions {
  clients: Array<{ id: string; code: string; companyName: string; companyNameAr: string | null }>;
  projects: Array<{ id: string; code: string; name: string; nameAr: string | null; clientId: string | null; status: string }>;
}

export interface DriveContact {
  id: string;
  name: string;
  jobTitle: string | null;
  email: string | null;
  isPrimary: boolean;
}

/** Every Drive query's key starts so: whatever changes in a drive refreshes them all. */
export const DRIVE_KEY = ['drive'] as const;

/** The admin's page of an item: a folder's contents, a file's own page. */
export function drivePage(item: { id: string; kind: DriveKind }): string {
  return item.kind === 'FOLDER' ? `/drive/folders/${item.id}` : `/drive/files/${item.id}`;
}

/** The clients and projects an item can be about. */
export function useDriveRelatedOptions() {
  return useQuery({
    queryKey: [...DRIVE_KEY, 'related-options'],
    queryFn: () => api.get<DriveRelatedOptions>('/drive/related-options'),
    staleTime: 60_000,
  });
}

/** A link's last moment from a day picked: the end of that day where the reader is. */
export function endOfDay(day: string): string | null {
  return day ? new Date(`${day}T23:59:59.999`).toISOString() : null;
}

/** The day a link's last moment falls on, for a date field. */
export function dayOf(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())}`;
}

/** `POST /drive/items/:id/download`: a link to a folder's .zip that works for two minutes, and what it holds. */
export interface DriveDownload {
  url: string;
  /** What it is saved as: the folder's name — `VLM.zip`. */
  name: string;
  files: number;
  folders: number;
  bytes: number;
}
